/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║         Nutrify — Product Spotlight Popup System            ║
 * ║                                                              ║
 * ║  Philosophy: helpful discovery, not manufactured urgency.   ║
 * ║  No fake countdown timers. No false scarcity.               ║
 * ║  Just the right product, at the right moment.               ║
 * ╚══════════════════════════════════════════════════════════════╝
 *
 * SHOPIFY INSTALL
 * ───────────────
 *  Upload this file to Content → Files in Shopify admin.
 *  Then include the snippet in your theme:
 *    {% render 'product-spotlight' %}  ← add to layout/theme.liquid before </body>
 *  The Liquid snippet injects SPOTLIGHT_CONFIG and SPOTLIGHT_PRODUCTS
 *  before this script runs, so config here acts as fallback defaults only.
 *
 * STANDALONE INSTALL (non-Shopify)
 * ─────────────────────────────────
 *  1. Edit SPOTLIGHT_PRODUCTS below with your real products.
 *  2. Paste before </body>:  <script src="product-spotlight.js"></script>
 *
 * TRACKING
 * ────────
 *  Events fire to localStorage, GA4 (gtag), and GTM (dataLayer) automatically.
 *  Read the log: JSON.parse(localStorage.getItem("nfy_spotlight_log"))
 */

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CONFIG + PRODUCTS
// ─────────────────────────────────────────────────────────────────
// Shopify:    product-spotlight.liquid sets window.SPOTLIGHT_CONFIG
//             and window.SPOTLIGHT_PRODUCTS before this deferred
//             script runs. Those values are used automatically.
//
// Standalone: edit the fallback objects below.
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Merge window values (from Liquid) with local defaults.
// window.* takes priority; local values are the standalone fallback.
const SPOTLIGHT_CONFIG = Object.assign(
  {
    triggerDelaySec:        2.5,
    reshowAfterDismissSec:  90,
    maxExposuresPerSession: 4,
    stopAfterClick:         true,
    enableUtm:              true,
    utmSource:              "nutrify_spotlight",
    sessionKey:             "nfy_spotlight_session",
    logKey:                 "nfy_spotlight_log",
    labels: [
      "Featured for you",
      "Popular pick",
      "Worth a look",
      "You might like this",
      "Customer favourite",
      "Editor's pick",
    ],
  },
  window.SPOTLIGHT_CONFIG || {}
);

// Prices are in the smallest currency unit (kobo for NGN).
// Shopify outputs variant.price in kobo already — no conversion needed.
const SPOTLIGHT_PRODUCTS = (window.SPOTLIGHT_PRODUCTS && window.SPOTLIGHT_PRODUCTS.length)
  ? window.SPOTLIGHT_PRODUCTS
  : [
    // ── Standalone fallback products (replace with your real data) ──
    {
      id: "green-blend-500",
      name: "Super Green Blend",
      benefit: "Daily greens, immunity, energy — all in one scoop",
      originalPrice: 850000,
      salePrice:     595000,
      discount:      "30% OFF",
      image: "https://via.placeholder.com/140x140/2d6a4f/ffffff?text=Green+Blend",
      ctaText: "See Product",
      ctaLink: "/products/super-green-blend",
      priority: "slow-mover",
      label: null,
    },
    {
      id: "collagen-boost",
      name: "Collagen Boost Powder",
      benefit: "Supports skin elasticity and joint health",
      originalPrice: 1200000,
      salePrice:     780000,
      discount:      "35% OFF",
      image: "https://via.placeholder.com/140x140/a7c957/ffffff?text=Collagen",
      ctaText: "View Details",
      ctaLink: "/products/collagen-boost",
      priority: "high-margin",
      label: "Customer favourite",
    },
    {
      id: "immunity-shots",
      name: "Immunity Shot Pack",
      benefit: "12 concentrated shots of zinc, C and elderberry",
      originalPrice: 960000,
      salePrice:     672000,
      discount:      "30% OFF",
      image: "https://via.placeholder.com/140x140/f4a261/ffffff?text=Immunity",
      ctaText: "Shop Now",
      ctaLink: "/products/immunity-shots",
      priority: "slow-mover",
      label: null,
    },
    {
      id: "fiber-complex",
      name: "Digestive Fiber Complex",
      benefit: "Prebiotic + soluble fibre for a happy gut",
      originalPrice: 720000,
      salePrice:     432000,
      discount:      "40% OFF",
      image: "https://via.placeholder.com/140x140/6d6875/ffffff?text=Fiber",
      ctaText: "Learn More",
      ctaLink: "/products/fiber-complex",
      priority: null,
      label: null,
    },
    {
      id: "protein-oats",
      name: "High-Protein Overnight Oats",
      benefit: "30g protein per serve — breakfast sorted",
      originalPrice: 550000,
      salePrice:     385000,
      discount:      "30% OFF",
      image: "https://via.placeholder.com/140x140/d4a373/ffffff?text=Oats",
      ctaText: "Try It",
      ctaLink: "/products/protein-oats",
      priority: "high-margin",
      label: "Worth a look",
    },
  ];

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STYLES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SPOTLIGHT_CSS = `
/* ── Tokens ─────────────────────────────────────────────────────── */
:root {
  --nsp-green:      #2d6a4f;
  --nsp-green-dark: #1b4332;
  --nsp-green-pale: #d8f3dc;
  --nsp-red:        #c0392b;
  --nsp-text:       #1a1a1a;
  --nsp-sub:        #6b7280;
  --nsp-border:     #e5e7eb;
  --nsp-radius:     16px;
  --nsp-shadow:     0 16px 48px rgba(0,0,0,.16), 0 4px 12px rgba(0,0,0,.08);
  --nsp-font:       'Segoe UI', system-ui, -apple-system, sans-serif;
}

/* ── Full popup ──────────────────────────────────────────────────── */
#nsp-popup {
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%) scale(.94);
  opacity: 0;
  width: min(400px, 92vw);
  background: #fff;
  border-radius: var(--nsp-radius);
  box-shadow: var(--nsp-shadow);
  font-family: var(--nsp-font);
  z-index: 99999;
  overflow: hidden;
  transition: transform .3s cubic-bezier(.34,1.36,.64,1), opacity .25s ease;
  pointer-events: none;
}
#nsp-popup.nsp-visible {
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
  pointer-events: auto;
}

/* Subtle backdrop — semi-transparent, not heavy */
#nsp-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.28);
  z-index: 99998;
  opacity: 0;
  transition: opacity .25s ease;
  pointer-events: none;
}
#nsp-backdrop.nsp-visible {
  opacity: 1;
  pointer-events: auto;
}

/* ── Header bar ──────────────────────────────────────────────────── */
#nsp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid var(--nsp-border);
}
#nsp-label-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--nsp-green-pale);
  color: var(--nsp-green-dark);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
}
#nsp-label-chip::before {
  content: '✦';
  font-size: 9px;
}
#nsp-header-actions { display: flex; gap: 4px; }

.nsp-icon-btn {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  background: none; border: none; border-radius: 8px;
  cursor: pointer; color: var(--nsp-sub);
  font-size: 16px; transition: background .15s, color .15s;
}
.nsp-icon-btn:hover { background: #f3f4f6; color: var(--nsp-text); }

/* ── Body ────────────────────────────────────────────────────────── */
#nsp-body {
  display: flex;
  gap: 16px;
  padding: 18px 18px 6px;
  align-items: flex-start;
}
#nsp-img-wrap {
  flex-shrink: 0;
  width: 120px; height: 120px;
  border-radius: 12px;
  overflow: hidden;
  background: #f9fafb;
}
#nsp-img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .3s ease;
}
#nsp-img-wrap:hover #nsp-img { transform: scale(1.04); }

#nsp-info { flex: 1; min-width: 0; }
#nsp-name {
  font-size: 16px; font-weight: 700;
  color: var(--nsp-text);
  margin: 0 0 5px;
  line-height: 1.3;
}
#nsp-benefit {
  font-size: 13px; color: var(--nsp-sub);
  margin: 0 0 12px;
  line-height: 1.5;
}

/* Pricing row */
#nsp-pricing {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
#nsp-price-sale {
  font-size: 22px; font-weight: 800;
  color: var(--nsp-green-dark);
}
#nsp-price-original {
  font-size: 13px; color: #9ca3af;
  text-decoration: line-through;
}
#nsp-price-badge {
  background: #fef2f2;
  color: var(--nsp-red);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px; font-weight: 700;
}

/* ── CTA ─────────────────────────────────────────────────────────── */
#nsp-cta-area {
  padding: 14px 18px 6px;
}
#nsp-cta {
  display: block;
  background: var(--nsp-green);
  color: #fff;
  text-align: center;
  padding: 13px 20px;
  border-radius: 10px;
  font-size: 14.5px; font-weight: 700;
  text-decoration: none;
  transition: background .2s, transform .15s;
  letter-spacing: .01em;
}
#nsp-cta:hover {
  background: var(--nsp-green-dark);
  transform: translateY(-1px);
}
#nsp-cta:active { transform: translateY(0); }

/* Sold-out note */
#nsp-sold-out-note {
  font-size: 11px; color: #6b7280; margin-top: 6px;
  background: #f3f4f6; border-radius: 6px; padding: 5px 8px;
}

/* Dismiss link */
#nsp-dismiss-area { text-align: center; padding: 6px 18px 16px; }
#nsp-dismiss {
  background: none; border: none;
  font-size: 12px; color: #9ca3af;
  cursor: pointer;
  font-family: var(--nsp-font);
  transition: color .15s;
}
#nsp-dismiss:hover { color: var(--nsp-sub); }

/* ── Mini widget (bottom-left) ──────────────────────────────────── */
#nsp-mini {
  position: fixed;
  bottom: 20px; left: 20px;
  width: 250px;
  background: #fff;
  border-left: 4px solid var(--nsp-green);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0,0,0,.18);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 34px 10px 10px;
  cursor: pointer;
  z-index: 99998;
  font-family: var(--nsp-font);
  transform: translateX(-110%);
  opacity: 0;
  transition: transform .4s cubic-bezier(.34,1.3,.64,1), opacity .3s ease;
}
#nsp-mini.nsp-visible {
  transform: translateX(0);
  opacity: 1;
}
#nsp-mini:hover { box-shadow: 0 10px 36px rgba(0,0,0,.24); }

#nsp-mini-close {
  position: absolute; top: 6px; right: 8px;
  background: none; border: none;
  font-size: 15px; cursor: pointer;
  color: #9ca3af; line-height: 1;
}
#nsp-mini-close:hover { color: var(--nsp-text); }

#nsp-mini-img {
  width: 42px; height: 42px;
  object-fit: cover; border-radius: 8px; flex-shrink: 0;
}
#nsp-mini-info { flex: 1; min-width: 0; }
#nsp-mini-name {
  font-size: 11.5px; font-weight: 700; color: var(--nsp-text);
  margin: 0 0 3px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
#nsp-mini-badge {
  display: inline-block;
  background: var(--nsp-green-pale);
  color: var(--nsp-green-dark);
  border-radius: 4px; padding: 1px 7px;
  font-size: 10px; font-weight: 700;
}
#nsp-mini-cta-hint {
  font-size: 10px; color: var(--nsp-sub);
  margin-top: 3px;
}

/* ── Mobile ──────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  #nsp-popup { width: 94vw; }
  #nsp-body { gap: 12px; }
  #nsp-img-wrap { width: 90px; height: 90px; }
  #nsp-name { font-size: 15px; }
  #nsp-mini { width: calc(100vw - 40px); bottom: 12px; left: 12px; }
}

/* ── Animations ──────────────────────────────────────────────────── */
@keyframes nspFadeIn  { from{opacity:0} to{opacity:1} }
@keyframes nspShimmer {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}
`;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// UTILITIES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function nspFormatNaira(kobo) {
  // Prices are stored in kobo. Remove /100 if you store whole Naira values.
  return "₦" + (kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 0 });
}

function nspTrackedUrl(product) {
  if (!SPOTLIGHT_CONFIG.enableUtm) return product.ctaLink;
  const p = new URLSearchParams({
    utm_source:   SPOTLIGHT_CONFIG.utmSource,
    utm_medium:   "spotlight_popup",
    utm_campaign: "product_spotlight",
    utm_content:  product.id,
  });
  const base = product.ctaLink;
  return base + (base.includes("?") ? "&" : "?") + p.toString();
}

/** Weighted random product pick — slow-movers and high-margin get priority */
function nspPickProduct(state) {
  // Stop if user already clicked a product this session
  if (state.stopped) return null;

  // Build weighted pool — exclude the last shown product to avoid repeats
  const pool = [];
  for (const p of SPOTLIGHT_PRODUCTS) {
    if (p.id === state.lastProductId) continue;
    const weight =
      p.priority === "slow-mover"  ? 3 :
      p.priority === "high-margin" ? 2 : 1;
    for (let i = 0; i < weight; i++) pool.push(p);
  }
  if (!pool.length) return SPOTLIGHT_PRODUCTS[0]; // fallback
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Get the label string for this popup showing */
function nspGetLabel(product, exposureCount) {
  if (product.label) return product.label;
  const labels = SPOTLIGHT_CONFIG.labels;
  return labels[exposureCount % labels.length];
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SESSION STATE  (sessionStorage — resets on new tab/session)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function nspGetState() {
  try {
    return JSON.parse(sessionStorage.getItem(SPOTLIGHT_CONFIG.sessionKey) || "{}");
  } catch { return {}; }
}

function nspSetState(patch) {
  const current = nspGetState();
  sessionStorage.setItem(
    SPOTLIGHT_CONFIG.sessionKey,
    JSON.stringify({ ...current, ...patch })
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// TRACKING
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function nspTrack(eventName, product, extra) {
  const entry = {
    event:       eventName,
    productId:   product?.id   ?? null,
    productName: product?.name ?? null,
    timestamp:   new Date().toISOString(),
    page:        window.location.pathname,
    ...extra,
  };

  // 1. localStorage log
  try {
    const log = JSON.parse(localStorage.getItem(SPOTLIGHT_CONFIG.logKey) || "[]");
    log.push(entry);
    // Keep last 200 entries to avoid bloat
    if (log.length > 200) log.splice(0, log.length - 200);
    localStorage.setItem(SPOTLIGHT_CONFIG.logKey, JSON.stringify(log));
  } catch (_) { /* storage might be full */ }

  // 2. Google Analytics 4
  if (typeof gtag === "function") {
    gtag("event", eventName, {
      event_category: "spotlight_popup",
      event_label:    product?.id,
      product_name:   product?.name,
      value:          product ? product.salePrice / 100 : undefined,
      currency:       "NGN",
    });
  }

  // 3. Google Tag Manager dataLayer
  if (typeof dataLayer !== "undefined") {
    dataLayer.push({
      event:        eventName,
      productId:    product?.id,
      productName:  product?.name,
      salePrice:    product ? product.salePrice / 100 : undefined,
    });
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// HTML BUILDERS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function nspBuildPopup(product, label) {
  const soldOut  = product.available === false;
  const ctaText  = soldOut ? "Notify Me When Back" : (product.ctaText || "View Product");
  // Sold-out CTA links to the product page where Shopify shows "Notify me" if set up
  const ctaHref  = nspTrackedUrl(product);
  const ctaStyle = soldOut
    ? "background:linear-gradient(135deg,#6b7280,#4b5563);"   // grey for sold-out
    : "";
  const discountHTML = product.discount
    ? `<span id="nsp-price-badge">${product.discount}</span>`
    : "";
  const originalHTML = (product.originalPrice && product.originalPrice !== product.salePrice)
    ? `<span id="nsp-price-original">${nspFormatNaira(product.originalPrice)}</span>`
    : "";
  const soldOutBadge = soldOut
    ? `<p id="nsp-sold-out-note">&#128337; Back soon — get notified when it's available</p>`
    : "";

  return `
<div id="nsp-backdrop"></div>
<div id="nsp-popup" role="dialog" aria-modal="true" aria-label="Product spotlight">

  <div id="nsp-header">
    <span id="nsp-label-chip">${label}</span>
    <div id="nsp-header-actions">
      <button class="nsp-icon-btn" id="nsp-minimize-btn" aria-label="Minimise" title="Minimise">&#8722;</button>
      <button class="nsp-icon-btn" id="nsp-close-btn"    aria-label="Close"    title="Close">&#10005;</button>
    </div>
  </div>

  <div id="nsp-body">
    <div id="nsp-img-wrap">
      <img id="nsp-img" src="${product.image}" alt="${product.name}" loading="lazy" />
    </div>
    <div id="nsp-info">
      <p id="nsp-name">${product.name}</p>
      <p id="nsp-benefit">${product.benefit}</p>
      <div id="nsp-pricing">
        <span id="nsp-price-sale">${nspFormatNaira(product.salePrice)}</span>
        ${originalHTML}
        ${discountHTML}
      </div>
      ${soldOutBadge}
    </div>
  </div>

  <div id="nsp-cta-area">
    <a id="nsp-cta" href="${ctaHref}" style="${ctaStyle}">${ctaText}</a>
  </div>
  <div id="nsp-dismiss-area">
    <button id="nsp-dismiss">Not now</button>
  </div>

</div>`;
}

function nspBuildMini(product) {
  const soldOut    = product.available === false;
  const badgeText  = soldOut ? "Back soon" : (product.discount || "");
  const hintText   = soldOut ? "Tap to get notified ›" : "Tap to view ›";

  return `
<div id="nsp-mini" role="complementary" aria-label="Product spotlight — click to expand">
  <button id="nsp-mini-close" aria-label="Dismiss">&#10005;</button>
  <img id="nsp-mini-img" src="${product.image}" alt="${product.name}" />
  <div id="nsp-mini-info">
    <p id="nsp-mini-name">${product.name}</p>
    <span id="nsp-mini-badge">${badgeText}</span>
    <p id="nsp-mini-cta-hint">${hintText}</p>
  </div>
</div>`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// CONTROLLER  (self-contained IIFE — no globals leaked)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

(function NspController() {

  let reshowTimer   = null;   // timer for delayed reshow after dismiss
  let currentProduct = null;  // product visible right now

  // ── Style injection ────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById("nsp-styles")) return;
    const s = document.createElement("style");
    s.id = "nsp-styles";
    s.textContent = SPOTLIGHT_CSS;
    document.head.appendChild(s);
  }

  // ── Element helpers ────────────────────────────────────────────

  function el(id) { return document.getElementById(id); }

  function removePopupDOM() {
    el("nsp-popup")?.remove();
    el("nsp-backdrop")?.remove();
  }

  function removeMiniDOM() {
    el("nsp-mini")?.remove();
  }

  // ── Animate in/out ─────────────────────────────────────────────

  function showPopupDOM() {
    // Trigger CSS transition on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el("nsp-popup")?.classList.add("nsp-visible");
        el("nsp-backdrop")?.classList.add("nsp-visible");
      });
    });
  }

  function hidePopupDOM(callback) {
    const popup    = el("nsp-popup");
    const backdrop = el("nsp-backdrop");
    if (!popup) { callback?.(); return; }
    popup.classList.remove("nsp-visible");
    backdrop?.classList.remove("nsp-visible");
    setTimeout(() => { removePopupDOM(); callback?.(); }, 320);
  }

  function showMiniDOM() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el("nsp-mini")?.classList.add("nsp-visible");
      });
    });
  }

  function hideMiniDOM(callback) {
    const mini = el("nsp-mini");
    if (!mini) { callback?.(); return; }
    mini.classList.remove("nsp-visible");
    setTimeout(() => { removeMiniDOM(); callback?.(); }, 420);
  }

  // ── Schedule reshow ────────────────────────────────────────────

  function scheduleReshow() {
    clearTimeout(reshowTimer);
    const state = nspGetState();
    if (state.stopped) return;
    if ((state.exposures || 0) >= SPOTLIGHT_CONFIG.maxExposuresPerSession) return;

    const delay = SPOTLIGHT_CONFIG.reshowAfterDismissSec * 1000;
    reshowTimer = setTimeout(() => showSpotlight(), delay);
  }

  // ── Mini widget ────────────────────────────────────────────────

  function showMini(product) {
    removeMiniDOM();
    document.body.insertAdjacentHTML("beforeend", nspBuildMini(product));
    showMiniDOM();

    // Clicking the mini body → re-open full popup
    el("nsp-mini").addEventListener("click", (e) => {
      if (e.target.id === "nsp-mini-close") return;
      nspTrack("mini_opened", product);
      hideMiniDOM(() => showSpotlight(product));
    });

    // Mini close → dismiss entirely, schedule reshow with new product
    el("nsp-mini-close").addEventListener("click", (e) => {
      e.stopPropagation();
      hideMiniDOM();
      scheduleReshow();
    });
  }

  // ── Main popup ─────────────────────────────────────────────────

  /**
   * showSpotlight — shows the full popup.
   * @param {object} [forceProduct]  Pass a product to show a specific one,
   *                                 otherwise the picker selects automatically.
   */
  function showSpotlight(forceProduct) {
    removePopupDOM();
    removeMiniDOM();
    clearTimeout(reshowTimer);

    const state = nspGetState();

    // Guard: stop after click
    if (state.stopped) return;

    // Guard: max exposures
    if ((state.exposures || 0) >= SPOTLIGHT_CONFIG.maxExposuresPerSession) return;

    const product = forceProduct || nspPickProduct(state);
    if (!product) return;

    const label   = nspGetLabel(product, state.exposures || 0);
    currentProduct = product;

    // Render
    document.body.insertAdjacentHTML("beforeend", nspBuildPopup(product, label));
    showPopupDOM();

    // Update session state
    nspSetState({
      exposures:     (state.exposures || 0) + 1,
      lastProductId: product.id,
    });

    nspTrack("popup_shown", product, { label, exposure: state.exposures || 0 });

    // ── Minimise button → show mini widget ───────────────────────
    el("nsp-minimize-btn").addEventListener("click", () => {
      nspTrack("popup_minimized", product);
      hidePopupDOM(() => showMini(product));
    });

    // ── Close button → dismiss, schedule reshow ──────────────────
    el("nsp-close-btn").addEventListener("click", () => {
      nspTrack("popup_dismissed", product, { via: "close_button" });
      hidePopupDOM(() => scheduleReshow());
    });

    // ── "Not now" link → dismiss, schedule reshow ────────────────
    el("nsp-dismiss").addEventListener("click", () => {
      nspTrack("popup_dismissed", product, { via: "not_now" });
      hidePopupDOM(() => scheduleReshow());
    });

    // ── Backdrop click → minimise (less aggressive than close) ───
    el("nsp-backdrop").addEventListener("click", () => {
      nspTrack("popup_minimized", product);
      hidePopupDOM(() => showMini(product));
    });

    // ── Escape key → minimise ────────────────────────────────────
    function onEscape(e) {
      if (e.key === "Escape") {
        document.removeEventListener("keydown", onEscape);
        nspTrack("popup_minimized", product);
        hidePopupDOM(() => showMini(product));
      }
    }
    document.addEventListener("keydown", onEscape);

    // ── CTA click → track and optionally stop showing ────────────
    el("nsp-cta").addEventListener("click", () => {
      nspTrack("product_clicked", product);
      if (SPOTLIGHT_CONFIG.stopAfterClick) {
        nspSetState({ stopped: true });
      }
    });
  }

  // ── Boot ───────────────────────────────────────────────────────

  function init() {
    injectStyles();
    const state = nspGetState();

    // Debug: confirm the script loaded and how many products are available.
    // You will see this in your browser console. Remove this line when live.
    console.log(
      "[Nutrify Spotlight] Script ready —",
      SPOTLIGHT_PRODUCTS.length, "products |",
      "exposures:", state.exposures || 0, "/", SPOTLIGHT_CONFIG.maxExposuresPerSession,
      "| stopped:", !!state.stopped
    );

    // Do not run if user already clicked a product this session
    if (state.stopped) {
      console.log("[Nutrify Spotlight] Paused — user already clicked a product this session.");
      return;
    }

    // Do not run if max exposures already reached
    if ((state.exposures || 0) >= SPOTLIGHT_CONFIG.maxExposuresPerSession) {
      console.log("[Nutrify Spotlight] Paused — max exposures reached for this session.");
      return;
    }

    if (SPOTLIGHT_PRODUCTS.length === 0) {
      console.warn("[Nutrify Spotlight] No products found — check your collection handle in the Liquid snippet.");
      return;
    }

    setTimeout(() => showSpotlight(), SPOTLIGHT_CONFIG.triggerDelaySec * 8000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Expose manual trigger for testing (remove in production if preferred)
  window.__nspShow   = showSpotlight;
  window.__nspReset  = () => {
    sessionStorage.removeItem(SPOTLIGHT_CONFIG.sessionKey);
    removePopupDOM();
    removeMiniDOM();
    clearTimeout(reshowTimer);
    console.log("[Spotlight] Session reset — call __nspShow() to trigger manually.");
  };

})();
